import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { renderQrCard } from '../lib/qrCard';

/**
 * Preview a printable "QR card" — title ("Bärenfang" + brand mark),
 * the QR itself with the brand mark embedded in the centre, and the
 * public calendar URL at the bottom — and give the admin two actions:
 *
 *   • Share (mobile) — uses the Web Share API with the composite PNG as
 *     a `File` so the OS picker (AirDrop, WhatsApp, Mail, …) opens.
 *     Only rendered when the browser advertises
 *     `navigator.canShare({ files })` support.
 *   • Download (desktop, plus a fallback everywhere) — triggers a normal
 *     `<a download>` click so the browser saves `qr-<name>.png` to disk.
 *
 * The composite is rendered via the shared `renderQrCard` helper in
 * `lib/qrCard.ts`, then exported as a base64 PNG used identically for
 * preview, share, and download.
 */
type Props = {
  name: string;
  /** Optional alias used as the login identifier in the QR's URL. Falls
   * back to `name` when missing, so callers that haven't been updated to
   * pass an alias keep working unchanged. */
  identifier?: string | null;
  onClose(): void;
};

function safeFilename(name: string): string {
  // Strip filesystem-unsafe characters on Windows/macOS/Linux; keep Unicode.
  return name.replace(/[\\/:*?"<>|]/g, '_');
}

export function QrCodeModal({ name, identifier, onClose }: Props) {
  const { t } = useTranslation();
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [shareErr, setShareErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<'png' | 'link' | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashCopied = (kind: 'png' | 'link') => {
    setCopied(kind);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), 2000);
  };

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  const loginIdentifier = identifier ?? name;
  const url = useMemo(
    () => `${window.location.origin}/anmelden?name=${encodeURIComponent(loginIdentifier)}`,
    [loginIdentifier],
  );
  // Footer URL = the public calendar's host (no protocol, easier to read on
  // a printed sheet). The QR itself encodes the full login URL above; this
  // is only a human-readable hint at the bottom of the card.
  const footerUrl = useMemo(
    () => window.location.host + '/',
    [],
  );

  // Generate the composite QR card once. Cancel via a flag so a fast close
  // doesn't leave a setState dangling on an unmounted component.
  useEffect(() => {
    let alive = true;
    renderQrCard(url, footerUrl)
      .then((d) => {
        if (alive) setDataUrl(d);
      })
      .catch(() => {
        if (alive) setDataUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [url, footerUrl]);

  // ESC closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filename = `qr-${safeFilename(name)}.png`;

  const onDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onShare = async () => {
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        // Pass `url` alongside the file so share targets (Mail, WhatsApp,
        // Notes…) attach the PNG AND drop the login URL into the message
        // body as a tappable link. Most targets render both; the ones that
        // only honour one usually prefer the URL.
        await navigator.share({
          files: [file],
          title: t('admin.hutDuty.qrTitle', { name }),
          text: name,
          url,
        });
      }
    } catch (e) {
      // User cancelled or share failed — surface a small inline message.
      const msg = e instanceof Error ? e.message : String(e);
      if (!/abort|cancel/i.test(msg)) setShareErr(msg);
    }
  };

  // The Share button only appears when the runtime actually supports
  // sharing PNG files (mobile Safari/Chrome, modern Android, some
  // desktop Edge). Desktop browsers without the API still get Download.
  const canShareFiles = useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.canShare) return false;
    try {
      const probe = new File([new Blob([''], { type: 'image/png' })], 'probe.png', {
        type: 'image/png',
      });
      return navigator.canShare({ files: [probe] });
    } catch {
      return false;
    }
  }, []);

  // Image-on-clipboard write is newer than text — Firefox shipped it in 127.
  // Gate the PNG-copy button on the actual capability; the link-copy button
  // uses writeText which is universal in the browsers this app targets.
  const canCopyImage = useMemo(
    () =>
      typeof navigator !== 'undefined' &&
      typeof navigator.clipboard?.write === 'function' &&
      typeof window.ClipboardItem !== 'undefined',
    [],
  );

  const onCopyPng = async () => {
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      flashCopied('png');
    } catch {
      setShareErr(t('admin.hutDuty.qrCopyFailed'));
    }
  };

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      flashCopied('link');
    } catch {
      setShareErr(t('admin.hutDuty.qrCopyFailed'));
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-modal-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 dark:bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-sm w-full"
      >
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3">
          <h3 id="qr-modal-title" className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t('admin.hutDuty.qrTitle', { name })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.cancel')}
            className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* The QR canvas itself stays dark-on-white in both modes so scanners
              read it reliably; only the surrounding modal chrome adapts. */}
          <div className="w-full bg-white border border-slate-200 rounded-md flex items-center justify-center min-h-[200px]">
            {dataUrl ? (
              <img
                src={dataUrl}
                alt={t('admin.hutDuty.qrTitle', { name })}
                className="w-full h-auto object-contain"
              />
            ) : (
              <span className="text-xs text-slate-400">…</span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 break-all tabular-nums">{url}</p>

          {shareErr && (
            <p
              role="alert"
              className="text-xs text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-2 py-1"
            >
              {shareErr}
            </p>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            {canShareFiles && (
              <button
                type="button"
                onClick={() => void onShare()}
                disabled={!dataUrl}
                className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                {t('admin.hutDuty.qrShare')}
              </button>
            )}
            {canCopyImage && (
              <button
                type="button"
                onClick={() => void onCopyPng()}
                disabled={!dataUrl}
                className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
              >
                {copied === 'png'
                  ? t('admin.hutDuty.qrCopied')
                  : t('admin.hutDuty.qrCopyPng')}
              </button>
            )}
            <button
              type="button"
              onClick={() => void onCopyLink()}
              className="rounded-md border border-slate-300 bg-white text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm font-medium"
            >
              {copied === 'link'
                ? t('admin.hutDuty.qrCopied')
                : t('admin.hutDuty.qrCopyLink')}
            </button>
            <button
              type="button"
              onClick={onDownload}
              disabled={!dataUrl}
              className="rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {t('admin.hutDuty.qrDownload')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
